import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import Text, {
  TEXT_COLOR,
  TEXT_SIZE,
  TEXT_KIND,
  TEXT_ALIGN,
} from '@/bit/components/components.text';
import { sendTaggingInfo } from './utils';
import { maskSeparation as applyMaskSeparation } from './utils';
import { calculateNewCaret } from './utils';

export const COMPONENT_NAME = 'text-area-form';
export const COMPONENT_THEME = {
  BOX: 'text-area-form--default',
  NO_BOX: 'text-area-form--no-box',
};
function TextArea({
  className,
  id,
  name,
  label,
  theme,
  mask,
  info,
  error,
  value,
  onFocus,
  onChange,
  onBlur,
  autoComplete,
  loaded,
  showError,
  readOnly,
  sendTagging,
  type,
  maxChars
}) {
  const [textAreaValue, setTextAreaValue] = useState('');
  const [key, setKey] = useState('');
  const taggingInfo = {
    componentVariation: 'all',
    componentProduct: '',
    componentName: 'text-area',
    componentContent: label,
  };

  useEffect(() => {
    setTextAreaValue(value);
  }, [value]);

  const handleOnFocus = () => {
    if(sendTagging){
      sendTaggingInfo(taggingInfo, 'focus');
    }
    onFocus();
  };

  const textAreaOnKeyDown = (e) => {
    setKey(e.key);
  };
  const handleOnChange = (event) => {
    const currentValue = event.target.value;
    let maskedValue = currentValue;
    if (mask) {
      const { target } = event;
      let newCaretPosition = target.selectionStart;
      const { maxLength, separationLength, separator } = mask;
      maskedValue = applyMaskSeparation(
        currentValue,
        maxLength,
        separationLength,
        separator
      );
      newCaretPosition = calculateNewCaret(
        key,
        newCaretPosition,
        separationLength
      );
      target.value = maskedValue;
      target.setSelectionRange(newCaretPosition, newCaretPosition);
    }
    setTextAreaValue(maskedValue);
    onChange(event);
  };

  const handleOnBlur = (event) => {
    onBlur(event);
  };
  const labelClassName = error
    ? 'text-area__label text-area__label--error'
    : 'text-area__label';
  return (
    <div className={`text-area ${className}`} data-cy={id}>
      <div className={`skeleton skeleton-text-area ${loaded ? 'loaded' : ''}`} />
      <div className={theme}>
        <textarea
          className="text-area__field"
          id={id}
          data-cy={`${id}__text-area`}
          maxLength={maxChars}
          name={name}
          value={textAreaValue}
          label={label}
          type={type}
          placeholder=" "
          onChange={handleOnChange}
          onBlur={handleOnBlur}
          onKeyDown={textAreaOnKeyDown}
          onFocus={handleOnFocus}
          autoComplete={autoComplete}
          readOnly={readOnly}
        />
        <label htmlFor={id} className={labelClassName} data-cy={`${id}__label`}>
          {label}
        </label>
        <div>
          <Text
            className="text-area__error"
            text={`${textAreaValue.length} / ${maxChars}`}
            color={TEXT_COLOR.GREY5}
            kind={TEXT_KIND.SPAN}
            align={TEXT_ALIGN.RIGHT}
            size={TEXT_SIZE.CAPTION3}
            cyData={`${id}__maxChracs`}
          />
        </div>
      </div>
      {showError && loaded && error && (
        <Text
          className="text-area__error"
          text={error}
          color={TEXT_COLOR.RED}
          kind={TEXT_KIND.SPAN}
          size={TEXT_SIZE.CAPTION3}
          cyData={`${id}__error`}
        />
      )}
      {loaded && info && !error && (
        <Text
          className="text-area__error line-height-120"
          text={info}
          color={TEXT_COLOR.GREY5}
          kind={TEXT_KIND.SPAN}
          size={TEXT_SIZE.CAPTION3}
          cyData={`${id}__info`}
        />
      )}
      <style jsx>{`
@import "styles/colors.scss";
.text-area {
  position: relative;
.text-area-form--default {
  background-color: $color--white;
  border-radius: 0.25rem;
  width: 100%;
  position: relative;
}
.text-area-form--default {
  background-color: $color--white;
  border-radius: 0.25rem;
  width: 100%;
  position: relative;
}
.skeleton-text-area {
  position: absolute;
  border-radius: 0.25rem;
  top: 0;
  left: 0;
}
.text-area__field {
  border: 0.0625rem solid $color--grey3;
  outline: none !important;
  font-size: 1rem;
  font-family: Regular;
  color: $color--grey6;
  padding: 1.5rem 1.25rem 0.5rem 0.75rem;
  background: transparent;
  width: 100%;
  border-radius: 0.25rem;
  cursor: pointer;
  min-height: 3.5rem;
  min-width: 12.5rem;
  max-width: 100%;; 
}

.text-area__field::placeholder {
  color: transparent;
}
.text-area__label {
  position: absolute;
  font-size: 1rem;
  font-family: Regular;
  cursor: text;
  pointer-events: none;
  white-space: nowrap;
  color: $color--grey5;
  width: 90%;
  overflow: hidden;
  text-overflow: ellipsis;
  background-color: white;
}
.text-area__field:placeholder-shown ~ .text-area__label {
  top: 1.0625rem;;
  left: 0.75rem;
}
.text-area__field:not(:placeholder-shown) ~ .text-area__label {
  top: 0.0625rem;
  padding: 0.1875rem 0.625rem;
  left: 0.125rem;
  border-radius: 0.25rem;
  width: calc(100% - 0.9375rem);
  font-size: 0.75rem;
  &--error {
    color: $color--red;
  }
}
.text-area__field:focus ~ .text-area__label {
  top: 0.0625rem !important;
  padding: 0.1875rem 0.625rem;
  left: 0.125rem;
  border-radius: 0.25rem;
  width: calc(100% - 0.9375rem);
  font-size: 0.75rem;
  color: $color--movistar_blue;
  &--error {
    color: $color--red;
  }
}
.text-area__field:focus {
  caret-color: $color--movistar_blue;
}
:global(.text-area__error) {
  padding: 0.25rem 0 0 0.75rem;
}
/* remove styles from autocomplete */
textarea:-webkit-autofill,
textarea:-webkit-autofill:hover,
textarea:-webkit-autofill:focus {
  -webkit-text-fill-color: $color--grey6 !important;
  -webkit-box-shadow: 0 0 0 62.5rem #ffffff inset;
}
/* remove styles from type number */
textarea::-webkit-outer-spin-button,
textarea::-webkit-inner-spin-button {
  -webkit-appearance: none;
  margin: 0;
}
textarea[type='number'] {
  -moz-appearance: textfield;
}

textarea {
  resize: none;
  height: 9.5rem;
}

@media screen and (min-width: 768px) {
  .text-area-form--default {
    height: auto;
  }
  .text-area-form--no-box {
    height: auto;
  }
  .text-area__field {
    padding: 1.25rem 1.25rem 0.25rem 0.75rem;
  }
  .text-area__field:placeholder-shown ~ .text-area__label {
    top: 0.875rem;
  }
}
}
`}</style>
      </div>
  );
}
TextArea.propTypes = {
  id: PropTypes.string.isRequired,
  name: PropTypes.string.isRequired,
  theme: PropTypes.string,
  className: PropTypes.string,
  label: PropTypes.string,
  onFocus: PropTypes.func,
  mask: PropTypes.object,
  error: PropTypes.string,
  info: PropTypes.string,
  onChange: PropTypes.func,
  onBlur: PropTypes.func,
  autoComplete: PropTypes.string,
  value: PropTypes.string,
  loaded: PropTypes.bool,
  showError: PropTypes.bool,
  readOnly: PropTypes.bool,
  sendTagging: PropTypes.bool,
  type: PropTypes.string,
  maxChars: PropTypes.number
};

TextArea.defaultProps = {
  className: '',
  label: 'text-area',
  theme: COMPONENT_THEME.BOX,
  error: '',
  info: '',
  mask: null,
  onChange: () => {},
  onBlur: () => {},
  onFocus: () => {},
  autoComplete: 'on',
  value: '',
  loaded: true,
  showError: true,
  readOnly: false,
  sendTagging: false,
  type: 'text',
  maxChars: 200
};
export default TextArea;
